import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Tabs, Tab } from "react-bootstrap";
import { OrgInfo, Sort, AdminApprovalGroup, MessageDisplayType, IActionHandler } from "@preveil-api";
import {
  Account, AdminErrorMessages, Message, useAppDispatch, useGetOrgInfo, sortBy, OrganizationInformation, getOppositeSortDirection, SORT_DIRECTION, MessageHandlerDisplayType,
  GlobalErrorMessages, MessageAnchors, UserResultsMode
} from "src/common";
import { PageHeader } from "src/components";
import { AssignTab, ManageTab } from ".";
import { adminActions, uiActions } from "src/store";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
  default_active_tab?: string;
};

function ApprovalGroupsComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const default_active_tab = location.state?.default_active_tab || "manage";
  const [create_modal_show, setCreateModalShow] = useState<boolean>(false);
  const [approval_groups, setApprovalGroups] = useState<AdminApprovalGroup[]>([]);
  const [organization_info, setOrganizationInfo] = useState<OrganizationInformation>();
  const [sort, setSort] = useState<Sort<keyof AdminApprovalGroup>>({ field: "name", direction: "asc" });

  const { data, error, refresh } = useGetOrgInfo(current_account, org_info, UserResultsMode.lightweight);

  /* Description: On initial load - call the use get org info hook to get the approval groups and requests. */
  useEffect(() => {
    refresh({
      history: false,
      status: "pending",
      hideExpired: true,
      limit: 200,
    });
    return () => {
      dispatch(adminActions.setSelected([]));
    };
  }, []);

  /* Description: This handles the data and error whenever the refresh() callback is triggered and sets all the 
  data in the components state. */
  useEffect(() => {
    if (!!data) {
      const sorted_groups = sortBy(
        data.approval_groups.filter((ag) => !ag.is_deleted),
        { field: sort.field, direction: sort.direction },
      );
      setApprovalGroups(sorted_groups);
      setOrganizationInfo(data);
      setIsLoading(false);
    }
    !!error &&
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_fetching_approval_groups), error)) && setIsLoading(false);
  }, [data, error]);

  // Description: set loading to true and call API call to fetch org info
  function handleRefresh() {
    setIsLoading(true);
    refresh({
      history: false,
      status: "pending",
      hideExpired: true,
      limit: 200,
    });
  }

  /* Description: Handle all children component actions and store it */
  const AdminApprovalGroupsCalls = {
    handleSetSort: (params: "name" | "approvers" | "required_approvers") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = sortBy(approval_groups, { field: params, direction });
      setSort({ field: params, direction });
      setApprovalGroups(sorted_requests);
    },
    /* Description: refresh the page by calling get logs */
    handleRefresh: () => {
      handleRefresh();
    },
    /* Description: open create modal */
    handleSetShow: () => {
      setCreateModalShow(!create_modal_show);
    },
    handlePageErrorMessage: (params: {
      message: string;
      stack?: any;
      display_type?: MessageDisplayType;
    }) => {
      const display_type = !!params.display_type
        ? params.display_type
        : MessageHandlerDisplayType.logger;
      dispatch(
        uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack),
      );
    },
  };

  function handlePageActions(action_obj: IActionHandler) {
    dispatch(uiActions.handleMessageDismiss());
    const callback = `handle${action_obj.actionType}`;
    // Handle local calls:
    if ((AdminApprovalGroupsCalls as any)[callback] instanceof Function) {
      (AdminApprovalGroupsCalls as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(
        MessageAnchors.actionType,
        action_obj.actionType,
      );
      AdminApprovalGroupsCalls.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return (
    <div className="content-wrapper">
      <PageHeader>
        <h1>Approval Groups</h1>
      </PageHeader>
      <Tabs defaultActiveKey={default_active_tab} id="approval-requests-tab">
        <Tab eventKey="manage" title="Manage" className="admin-component">
          <ManageTab
            current_account={current_account}
            org_info={org_info}
            approval_groups={approval_groups}
            setApprovalGroups={setApprovalGroups}
            organization_info={organization_info}
            handleParentAction={handlePageActions}
            sort={sort}
            is_loading={is_loading}
            create_modal_show={create_modal_show}
          ></ManageTab>
        </Tab>
        <Tab eventKey="assign" title="Assign" className="tab-assign">
          {
            !!organization_info &&
            <AssignTab
              approval_groups={approval_groups}
              organization_info={organization_info}
              is_loading={is_loading}
              handleAction={handlePageActions}
              current_account={current_account}
              org_info={org_info} />
          }
        </Tab>
      </Tabs>
    </div>
  );
}

export default React.memo(ApprovalGroupsComponent);
